import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import '../company/CompanyForm.css';
import './PublicationSection.css';
import {
  listPublications,
  createPublication,
  updatePublication,
  deletePublication,
  setPublicationStatus,
  getPublicationViewCount,
} from '../../../services/publicationsApi';
import { promoteResource, unpromoteResource } from '../../../services/promotionsApi';
import { listUnits, UNIT_TYPES } from '../../../services/unitsApi';
import DateObject from 'react-date-object';
import {
  FiEye,
  FiTag,
  FiTruck,
  FiShield,
  FiLifeBuoy,
  FiBox,
  FiFileText,
  FiGlobe,
  FiCheckCircle,
  FiPauseCircle,
  FiLayers,
  FiInfo,
  FiPlus,
  FiSearch,
} from 'react-icons/fi';
import { FaCalendarAlt, FaStickyNote } from 'react-icons/fa';
import {
  DEFAULT_PUBLICATION_TYPE,
  PUBLICATION_TYPES,
  PUBLICATION_CATEGORY_RULES,
  normalizePublicationType,
  isProductPublication,
  isServicePublication,
} from '../../../constants/publicationTypes';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput';
import RichTextEditor from '../../../components/RichTextEditor';
import { normalizeAvailabilityRanges } from '../../../utils/availabilityRanges';
import {
  ensureRichText,
  normalizeRichTextInput,
  richTextToPlainText,
} from '../../../utils/richText';
import usePromotionLookup from '../../../hooks/usePromotionLookup';
import { useLocation, useNavigate } from 'react-router-dom';
import TableColumnMenu from '../../../components/TableColumnMenu';
import useTableColumns from '../../../hooks/useTableColumns';

const TYPE_OPTIONS = PUBLICATION_TYPES;

const hidesPriceForType = value => {
  const normalized = normalizePublicationType(value);
  return [
    'service_ground_transport',
    'service_security',
    'service_road_assistance',
    'customs_agent',
    'freight_forwarder',
  ].includes(normalized);
};

const STATUS_LABELS = {
  draft: 'Borrador',
  published: 'Activa',
  active: 'Activa',
  paused: 'Pausada',
  archived: 'Archivada',
};
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const PUBLICATION_COLUMN_DEFAULTS = [
  { key: 'info', label: 'Publicacion', width: 280, minWidth: 220 },
  { key: 'status', label: 'Estado', width: 170, minWidth: 150 },
  { key: 'views', label: 'Visitas', width: 150, minWidth: 130 },
  { key: 'createdBy', label: 'Creada por', width: 200, minWidth: 170 },
  { key: 'updatedBy', label: 'Actualizada por', width: 200, minWidth: 170 },
  { key: 'updatedAt', label: 'Actualizado', width: 160, minWidth: 140 },
];

const KPI_STORAGE_KEY = 'flotte:publicationKpiCollapsed';

const UNIT_TYPE_LABELS = UNIT_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const PUBLICATION_TYPE_META = {
  service_ground_transport: {
    title: 'Servicio terrestre',
    description: 'Rutas, fletes y soluciones por carretera.',
    icon: FiTruck,
  },
  service_security: {
    title: 'Custodia y escoltas',
    description: 'Proteccion dedicada para tus operaciones.',
    icon: FiShield,
  },
  service_road_assistance: {
    title: 'Rescate y asistencia',
    description: 'Soporte vial disponible 24/7.',
    icon: FiLifeBuoy,
  },
  customs_agent: {
    title: 'Agente aduanal',
    description: 'Tramites, liberaciones y asesoria fronteriza.',
    icon: FiFileText,
  },
  freight_forwarder: {
    title: 'Freight forwarder',
    description: 'Coordinacion multimodal y soluciones integrales.',
    icon: FiGlobe,
  },
  product: {
    title: 'Productos y refacciones',
    description: 'Pon en vitrina tus unidades o inventario.',
    icon: FiBox,
  },
};

const emptyFaq = () => ({ question: '', answer: '' });

const TITLE_MAX_LENGTH = 48;
const DESCRIPTION_MAX_LENGTH = 2000;

const PRODUCT_AVAILABILITY_MODES = {
  ALWAYS: 'always',
  RANGE: 'range',
};

const FIELD_SOURCE = {
  UNIT: 'unit',
  CUSTOM: 'custom',
};

const UNIT_REFERENCE_TOOLTIP =
  'Usa este campo para identificar la unidad o servicio en tu catalogo. Se muestra solo para ti; el titulo es lo que ven tus clientes.';

const BASE_EMPTY_FORM = {
  title: '',
  description: '',
  type: DEFAULT_PUBLICATION_TYPE,
  unitName: '',
  unitId: '',
  unitAvailabilityRanges: [],
  location: '',
  availableFrom: '',
  availableTo: '',
  autoPauseAt: '',
  faqs: [emptyFaq()],
  policies: '',
  sla: '',
  price: '',
  priceSource: FIELD_SOURCE.CUSTOM,
  slaSource: FIELD_SOURCE.CUSTOM,
  status: 'published',
  bannerPreference: 'company',
  bannerUrl: '',
  productImageFile: null,
  productImageUrl: '',
  productImageRemoved: false,
  createdBy: '',
  availabilityMode: PRODUCT_AVAILABILITY_MODES.ALWAYS,
};

const createEmptyForm = (type = DEFAULT_PUBLICATION_TYPE) => {
  const normalizedType = normalizePublicationType(type);
  return {
    ...BASE_EMPTY_FORM,
    type: normalizedType,
    availabilityMode: isServicePublication(normalizedType)
      ? PRODUCT_AVAILABILITY_MODES.RANGE
      : PRODUCT_AVAILABILITY_MODES.ALWAYS,
  };
};

const AVAILABILITY_FORMAT = 'YYYY-MM-DD';

const ALL_PUBLICATION_TYPES = PUBLICATION_CATEGORY_RULES.logistics_operator || [
  DEFAULT_PUBLICATION_TYPE,
];

const MEMBERSHIP_AVAILABILITY_LIMITS = {
  pro: 5,
  premium: 7,
  elite: 14,
};

const DEFAULT_AVAILABILITY_LIMIT_DAYS = 3;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const normalizeCategoryList = value => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  const raw = String(value).trim();
  if (!raw) return [];
  if ((raw.startsWith('[') && raw.endsWith(']')) || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item).trim()).filter(Boolean);
      }
    } catch {
      // ignore parse errors, fallback below
    }
  }
  return raw
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

export default function PublicationSection({
  user,
  empresaId,
  empresaCategory,
  empresaCategories,
  empresaPlan,
}) {
  const companyCategories = useMemo(() => {
    const combined = new Set();
    normalizeCategoryList(empresaCategory).forEach(category => combined.add(category));
    normalizeCategoryList(empresaCategories).forEach(category => combined.add(category));
    return Array.from(combined);
  }, [empresaCategory, empresaCategories]);

  const normalizedPlan = useMemo(
    () => String(empresaPlan || '').trim().toLowerCase(),
    [empresaPlan]
  );

  const availabilityLimitDays = useMemo(
    () => MEMBERSHIP_AVAILABILITY_LIMITS[normalizedPlan] || DEFAULT_AVAILABILITY_LIMIT_DAYS,
    [normalizedPlan]
  );

  const availabilityLimitLabel = useMemo(
    () => `${availabilityLimitDays} ${availabilityLimitDays === 1 ? 'día' : 'días'}`,
    [availabilityLimitDays]
  );

  const allowedTypeValues = useMemo(() => {
    if (companyCategories.includes('logistics_operator')) {
      return [...ALL_PUBLICATION_TYPES];
    }

    const collected = new Set();
    companyCategories.forEach(category => {
      const allowedForCategory = PUBLICATION_CATEGORY_RULES[category];
      if (allowedForCategory) {
        allowedForCategory.forEach(typeValue => collected.add(typeValue));
      }
    });

    if (!collected.size) {
      // Fallback: si no hay categoria clara, limitamos a producto.
      return ['product'];
    }

    return Array.from(collected);
  }, [companyCategories]);

  const visibleTypeOptions = useMemo(
    () => TYPE_OPTIONS.filter(option => allowedTypeValues.includes(option.value)),
    [allowedTypeValues]
  );

  const defaultType = visibleTypeOptions[0]?.value || DEFAULT_PUBLICATION_TYPE;

  const [form, setForm] = useState(() => createEmptyForm(defaultType));
  const [formBaseline, setFormBaseline] = useState(() => createEmptyForm(defaultType));
  const [list, setList] = useState([]);
  const [viewCounts, setViewCounts] = useState({});
  const [units, setUnits] = useState([]);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [mode, setMode] = useState('list');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ key: 'info', direction: 'asc' });
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [productImagePreview, setProductImagePreview] = useState('');
  const { isPromoted, refresh: refreshPromotions } = usePromotionLookup();
  const [promotionLoading, setPromotionLoading] = useState({});
  const productImagePreviewRef = useRef('');
  const [statusMenuOpen, setStatusMenuOpen] = useState('');
  const [statusUpdating, setStatusUpdating] = useState({});
  const statusMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [showDurationStartHint, setShowDurationStartHint] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const creatorId = user?.id || '';
  const [activeColumnMenu, setActiveColumnMenu] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(KPI_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const publicationSearchInputId = 'publication-search-input';
  const columnHeaderRefs = useRef({});
  const columnResizeStateRef = useRef({
    columnKey: '',
    startX: 0,
    startWidth: 0,
  });
  const restoredUnitSlaSourceRef = useRef(false);
  const {
    columns: publicationColumns,
    visibleColumns: visiblePublicationColumns,
    moveColumn: movePublicationColumn,
    toggleColumn: togglePublicationColumn,
    setColumnWidth: setPublicationColumnWidth,
    resetColumns: resetPublicationColumns,
  } = useTableColumns(
    `publications.${empresaId || creatorId || 'default'}`,
    PUBLICATION_COLUMN_DEFAULTS
  );

  const toggleColumnMenu = useCallback(columnKey => {
    setActiveColumnMenu(prev => (prev === columnKey ? '' : columnKey));
  }, []);

  const handleColumnContextMenu = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      toggleColumnMenu(columnKey);
    },
    [toggleColumnMenu]
  );

  const handleColumnSort = useCallback(columnKey => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const handleColumnHeaderKeyDown = useCallback(
    (event, columnKey) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleColumnSort(columnKey);
        return;
      }
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault();
        toggleColumnMenu(columnKey);
      }
    },
    [handleColumnSort, toggleColumnMenu]
  );

  const beginColumnResize = useCallback(
    (clientX, column) => {
      if (typeof clientX !== 'number') return;
      columnResizeStateRef.current = {
        columnKey: column.key,
        startX: clientX,
        startWidth: column.width || column.minWidth || 180,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.add('is-resizing-column');
      }
    },
    []
  );

  const handleColumnResizeMouseDown = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.clientX, column);
    },
    [beginColumnResize]
  );

  const handleColumnResizeTouchStart = useCallback(
    (event, column) => {
      if (!event.touches || event.touches.length !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.touches[0].clientX, column);
    },
    [beginColumnResize]
  );

  useEffect(() => {
    const stopResizing = () => {
      if (!columnResizeStateRef.current.columnKey) return;
      columnResizeStateRef.current = {
        columnKey: '',
        startX: 0,
        startWidth: 0,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.remove('is-resizing-column');
      }
    };

    const handlePointerMove = event => {
      const { columnKey, startWidth, startX } = columnResizeStateRef.current;
      if (!columnKey) return;
      const clientX =
        'touches' in event ? event.touches?.[0]?.clientX : event.clientX;
      if (typeof clientX !== 'number') return;
      event.preventDefault();
      const delta = clientX - startX;
      setPublicationColumnWidth(columnKey, startWidth + delta);
    };

    const handlePointerUp = event => {
      if (!columnResizeStateRef.current.columnKey) {
        return;
      }
      if (event) {
        event.preventDefault();
      }
      stopResizing();
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    window.addEventListener('blur', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
      window.removeEventListener('blur', stopResizing);
      stopResizing();
    };
  }, [setPublicationColumnWidth]);

  const handlePageSizeChange = useCallback(event => {
    setPageSize(Number(event.target.value));
  }, []);

  const getSortValue = useCallback(
    (item, columnKey) => {
      if (!item) return '';
      switch (columnKey) {
        case 'info': {
          const label =
            item.title ||
            item.unitName ||
            item.unit?.model ||
            item.unit?.tractorType ||
            item.unit?.economicNumber ||
            item.id ||
            '';
          return label.toString().toLowerCase();
        }
        case 'status': {
          const normalizedStatus = String(item.status || '').toLowerCase();
          const statusLabel = STATUS_LABELS[normalizedStatus] || item.status || '';
          return statusLabel.toString().toLowerCase();
        }
        case 'views': {
          const overrideViews = viewCounts?.[item.id];
          const rawViews = Number(item.views ?? item.viewCount ?? 0);
          const normalizedViews =
            typeof overrideViews === 'number' && Number.isFinite(overrideViews)
              ? Math.max(0, Math.round(overrideViews))
              : Number.isFinite(rawViews)
              ? Math.max(0, Math.round(rawViews))
              : 0;
          return normalizedViews;
        }
        case 'createdBy': {
          const label =
            item.createdByUser?.name ||
            item.createdByUser?.username ||
            item.createdByUser?.email ||
            'Sin registrar';
          return label.toString().toLowerCase();
        }
        case 'updatedBy': {
          const label =
            item.updatedByUser?.name ||
            item.updatedByUser?.username ||
            item.updatedByUser?.email ||
            item.createdByUser?.name ||
            item.createdByUser?.username ||
            item.createdByUser?.email ||
            'Sin registrar';
          return label.toString().toLowerCase();
        }
        case 'updatedAt': {
          const dateValue = item.updated || item.created;
          const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
          return Number.isNaN(timestamp) ? 0 : timestamp;
        }
        default:
          return '';
      }
    },
    [viewCounts]
  );

  const clearProductImagePreview = useCallback(() => {
    if (productImagePreviewRef.current) {
      URL.revokeObjectURL(productImagePreviewRef.current);
      productImagePreviewRef.current = '';
    }
    setProductImagePreview('');
  }, []);

  const applyTypeChange = useCallback(
    nextTypeValue => {
      const normalizedNext = normalizePublicationType(nextTypeValue);
      setForm(prev => {
        const nextState = {
          ...prev,
          type: normalizedNext,
        };
        if (isServicePublication(normalizedNext) && normalizedNext !== 'service_road_assistance') {
          nextState.availabilityMode = PRODUCT_AVAILABILITY_MODES.RANGE;
        } else {
          nextState.availabilityMode = PRODUCT_AVAILABILITY_MODES.ALWAYS;
        }
        if (!isServicePublication(normalizedNext)) {
          nextState.availableFrom = '';
          nextState.availableTo = '';
          nextState.autoPauseAt = '';
        }
        if (
          !isProductPublication(normalizedNext) &&
          normalizedNext !== 'service_road_assistance'
        ) {
          nextState.productImageFile = null;
          nextState.productImageUrl = '';
          nextState.productImageRemoved = false;
        }
        if (hidesPriceForType(normalizedNext)) {
          nextState.price = '';
        }
        return nextState;
      });
      if (!isServicePublication(normalizedNext)) {
        setSelectedUnitId('');
      }
      if (
        !isProductPublication(normalizedNext) &&
        normalizedNext !== 'service_road_assistance'
      ) {
        clearProductImagePreview();
      }
      setActiveTab(prev => {
        let nextTab = prev;
        if (prev === 'availability' && !isServicePublication(normalizedNext)) {
          nextTab = 'general';
        }
        if (
          nextTab === 'media' &&
          !(
            isProductPublication(normalizedNext) ||
            normalizedNext === 'service_road_assistance'
          )
        ) {
          nextTab = 'general';
        }
        if (nextTab === 'price' && hidesPriceForType(normalizedNext)) {
          nextTab = 'general';
        }
        return nextTab;
      });
    },
    [clearProductImagePreview]
  );

  const setPromotionLoadingFor = useCallback((id, loading) => {
    if (!id) return;
    setPromotionLoading(prev => {
      if (loading) {
        if (prev[id]) {
          return prev;
        }
        return { ...prev, [id]: true };
      }
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setStatusUpdatingFor = useCallback((id, loading) => {
    if (!id) return;
    setStatusUpdating(prev => {
      if (loading) {
        if (prev[id]) {
          return prev;
        }
        return { ...prev, [id]: true };
      }
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    const normalizedCurrentType = normalizePublicationType(form.type);
    if (!allowedTypeValues.includes(normalizedCurrentType)) {
      applyTypeChange(defaultType);
    }
  }, [allowedTypeValues, defaultType, form.type, applyTypeChange]);

  useEffect(() => {
    if (!statusMenuOpen) {
      return undefined;
    }
    const handleClickOutside = event => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setStatusMenuOpen('');
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setStatusMenuOpen('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [statusMenuOpen, statusMenuRef, setStatusMenuOpen]);

  const normalizedFormType = normalizePublicationType(form.type);
  const isServiceRoadAssistance = normalizedFormType === 'service_road_assistance';
  const isProductTypeSelected = isProductPublication(normalizedFormType);
  const requiresAvailability = isServicePublication(normalizedFormType) && !isServiceRoadAssistance;
  const productAvailabilityMode =
    form.availabilityMode ||
    (isProductTypeSelected ? PRODUCT_AVAILABILITY_MODES.ALWAYS : PRODUCT_AVAILABILITY_MODES.RANGE);
  const productHasLimitedAvailability =
    isProductTypeSelected && productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.RANGE;
  const shouldCaptureAvailability = requiresAvailability || productHasLimitedAvailability;
  const showPolicies = false;
  const viewFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    []
  );
  const autoPauseFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    []
  );
  const durationOptions = useMemo(
    () =>
      Array.from({ length: availabilityLimitDays }, (_, index) => index + 1),
    [availabilityLimitDays]
  );
  const hasUnsavedChanges = useMemo(() => {
    try {
      return JSON.stringify(formBaseline) !== JSON.stringify(form);
    } catch (err) {
      return false;
    }
  }, [form, formBaseline]);

  const filteredList = useMemo(() => {
    if (!Array.isArray(list)) {
      return [];
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return list;
    }
    return list.filter(item => {
      const fields = [
        item.title,
        item.unitName,
        item.location,
        item.type,
        item.status,
        item.createdByUser?.name,
        item.createdByUser?.username,
        item.createdByUser?.email,
        item.updatedByUser?.name,
        item.updatedByUser?.username,
        item.updatedByUser?.email,
      ];
      return fields.some(value => {
        if (!value && value !== 0) return false;
        return value.toString().toLowerCase().includes(query);
      });
    });
  }, [list, searchQuery]);

  const sortedList = useMemo(() => {
    if (!Array.isArray(filteredList)) return [];
    const next = [...filteredList];
    if (!sortConfig?.key) {
      return next;
    }
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      const valueA = getSortValue(a, sortConfig.key);
      const valueB = getSortValue(b, sortConfig.key);
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA === valueB) return 0;
        return valueA > valueB ? direction : -direction;
      }
      const stringA = valueA?.toString().toLowerCase() || '';
      const stringB = valueB?.toString().toLowerCase() || '';
      const comparison = stringA.localeCompare(stringB, 'es', { sensitivity: 'base' });
      if (comparison === 0) return 0;
      return comparison > 0 ? direction : -direction;
    });
    return next;
  }, [filteredList, getSortValue, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedList.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [pageSize, sortConfig]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const paginatedList = useMemo(() => {
    if (loading) return [];
    const start = page * pageSize;
    return sortedList.slice(start, start + pageSize);
  }, [loading, page, pageSize, sortedList]);

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index),
    [totalPages]
  );

  useEffect(() => {
    if (!paginatedList.length) {
      return undefined;
    }

    let ignore = false;
    const fetchViewCounts = async () => {
      try {
        const entries = await Promise.all(
          paginatedList
            .map(item => item.id)
            .filter(Boolean)
            .map(async id => {
              const total = await getPublicationViewCount(id);
              return [id, total];
            })
        );
        if (ignore || !entries.length) {
          return;
        }
        setViewCounts(prev => {
          const next = { ...prev };
          let changed = false;
          entries.forEach(([id, count]) => {
            if (typeof count === 'number' && Number.isFinite(count) && next[id] !== count) {
              next[id] = count;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      } catch (fetchError) {
        console.warn('No se pudieron actualizar las visitas de las publicaciones visibles.', fetchError);
      }
    };

    fetchViewCounts();

    return () => {
      ignore = true;
    };
  }, [paginatedList]);

  const searchActive = Boolean(searchQuery.trim());
  const showEmptyState = !loading && list.length === 0;
  const showSearchNoResults =
    !loading && searchActive && list.length > 0 && filteredList.length === 0;
  const renderPlaceholderRow = useCallback(
    key => (
      <tr key={`placeholder-${key}`} className="table-placeholder-empty">
        <td colSpan={Math.max(1, visiblePublicationColumns.length)} aria-hidden="true">
          &nbsp;
        </td>
      </tr>
    ),
    [visiblePublicationColumns.length]
  );

  useEffect(() => {
    if (!shouldCaptureAvailability && activeTab === 'availability') {
      setActiveTab('general');
    }
  }, [shouldCaptureAvailability, activeTab]);

  const loadPublications = async () => {
    if (!empresaId && !creatorId) {
      setList([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (empresaId) {
        params.companyId = empresaId;
      } else if (creatorId) {
        params.creatorId = creatorId;
      }
      const items = await listPublications(params);
      const normalizedItems = items.map(item => ({
        ...item,
        type: normalizePublicationType(item.type),
      }));
      setList(normalizedItems);
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar las publicaciones.');
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  setViewCounts({});
}, [creatorId, empresaId]);

useEffect(() => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KPI_STORAGE_KEY, kpiCollapsed ? 'true' : 'false');
  } catch {
    // ignore write errors
  }
}, [kpiCollapsed]);

useEffect(() => {
  loadPublications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [creatorId, empresaId]);

  useEffect(() => {
    let ignore = false;

    if (!empresaId && !creatorId) {
      setUnits([]);
      setUnitsLoaded(true);
      return undefined;
    }

    setUnitsLoaded(false);
    const fetchUnits = async () => {
      try {
        const response = await listUnits(
          empresaId ? { companyId: empresaId } : { createdBy: creatorId }
        );
        if (!ignore) {
          setUnits(response);
          setUnitsLoaded(true);
        }
      } catch (unitError) {
        if (!ignore) {
          console.error('No se pudieron cargar las unidades para sugerencias.', unitError);
          setUnits([]);
          setUnitsLoaded(true);
        }
      }
    };

    fetchUnits();

    return () => {
      ignore = true;
    };
  }, [creatorId, empresaId]);

  const unitOptions = useMemo(
    () =>
      units.map(unit => {
        const descriptor = [
          unit.economicNumber,
          unit.model,
          unit.capacity,
          UNIT_TYPE_LABELS[unit.tractorType] || unit.tractorType,
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          id: unit.id,
          label: descriptor || unit.model || unit.id,
        };
      }),
    [units]
  );

  const selectedUnit = useMemo(() => {
    const searchId = form.unitId || selectedUnitId;
    if (!searchId) return null;
    return units.find(unit => unit.id === searchId) || null;
  }, [form.unitId, selectedUnitId, units]);

  const hasLinkedUnit = Boolean(form.unitId || selectedUnitId);
  const unitPriceLabel = selectedUnit?.priceLabel || '';
  const unitSlaValue = selectedUnit?.sla || '';
  const canUseUnitPrice = hasLinkedUnit && Boolean(unitPriceLabel);
  const canUseUnitSla = hasLinkedUnit && Boolean(unitSlaValue);

  useEffect(() => {
    if (!requiresAvailability) {
      setForm(prev => {
        if (Array.isArray(prev.unitAvailabilityRanges) && prev.unitAvailabilityRanges.length) {
          return { ...prev, unitAvailabilityRanges: [] };
        }
        return prev;
      });
      return;
    }

    const activeUnitId = form.unitId || selectedUnitId;
    if (!activeUnitId) {
      setForm(prev => {
        if (Array.isArray(prev.unitAvailabilityRanges) && prev.unitAvailabilityRanges.length) {
          return { ...prev, unitAvailabilityRanges: [] };
        }
        return prev;
      });
      return;
    }

    if (!selectedUnit) {
      return;
    }

    const nextRanges = normalizeAvailabilityRanges(selectedUnit.availabilityRanges);
    setForm(prev => {
      const current = normalizeAvailabilityRanges(prev.unitAvailabilityRanges || []);
      if (JSON.stringify(current) === JSON.stringify(nextRanges)) {
        return prev;
      }
      return { ...prev, unitAvailabilityRanges: nextRanges };
    });
  }, [requiresAvailability, selectedUnit, form.unitId, selectedUnitId]);

  const selectedUnitPhoto = selectedUnit?.photoUrl || '';
  const bannerPreference = form.bannerPreference;
  const hasStoredUnitBanner = bannerPreference === 'unit' && Boolean(form.bannerUrl);
  const unitBannerAvailable =
    Boolean(selectedUnitPhoto) || (!unitsLoaded && hasStoredUnitBanner);

  useEffect(() => {
    if (bannerPreference !== 'unit') {
      setForm(prev => (prev.bannerUrl ? { ...prev, bannerUrl: '' } : prev));
    }
  }, [bannerPreference]);

  useEffect(() => {
    if (bannerPreference !== 'unit' || !unitsLoaded) {
      return;
    }
    setForm(prev => {
      const nextUrl = selectedUnitPhoto || '';
      if (prev.bannerUrl === nextUrl) {
        return prev;
      }
      return {
        ...prev,
        bannerUrl: nextUrl,
      };
    });
  }, [selectedUnitPhoto, bannerPreference, unitsLoaded]);

  useEffect(() => {
    if (!unitsLoaded && form.priceSource === FIELD_SOURCE.UNIT) {
      return;
    }
    if (!canUseUnitPrice && form.priceSource === FIELD_SOURCE.UNIT) {
      setForm(prev =>
        prev.priceSource === FIELD_SOURCE.UNIT ? { ...prev, priceSource: FIELD_SOURCE.CUSTOM } : prev
      );
    }
  }, [canUseUnitPrice, form.priceSource, unitsLoaded]);

  useEffect(() => {
    if (!unitsLoaded && form.slaSource === FIELD_SOURCE.UNIT) {
      return;
    }
    if (!canUseUnitSla && form.slaSource === FIELD_SOURCE.UNIT) {
      setForm(prev =>
        prev.slaSource === FIELD_SOURCE.UNIT ? { ...prev, slaSource: FIELD_SOURCE.CUSTOM } : prev
      );
    }
  }, [canUseUnitSla, form.slaSource, unitsLoaded]);

  useEffect(() => {
    restoredUnitSlaSourceRef.current = false;
  }, [editingId]);

  useEffect(() => {
    if (!editingId) return;
    if (restoredUnitSlaSourceRef.current) return;
    if (!unitsLoaded || !selectedUnit) return;
    if (form.slaSource !== FIELD_SOURCE.CUSTOM) return;
    const trimmedUnitSla = (selectedUnit.sla || '').trim();
    const trimmedFormSla = (form.sla || '').trim();
    if (!trimmedUnitSla || !trimmedFormSla) return;
    if (trimmedUnitSla !== trimmedFormSla) return;
    setForm(prev => ({
      ...prev,
      slaSource: FIELD_SOURCE.UNIT,
    }));
    restoredUnitSlaSourceRef.current = true;
  }, [editingId, unitsLoaded, selectedUnit, form.sla, form.slaSource]);

  const toDateObject = useCallback(value => {
    if (!value) return null;
    try {
      if (value instanceof DateObject) {
        return new DateObject(value);
      }
      return new DateObject({ date: value, format: AVAILABILITY_FORMAT });
    } catch (err) {
      return null;
    }
  }, []);

  const formatAvailabilityDate = useCallback(
    value => {
      const date = toDateObject(value);
      return date ? date.format('DD MMM YYYY') : '';
    },
    [toDateObject]
  );

  const buildAutoPauseAt = useCallback(
    value => {
      if (!value) return '';
      const date = toDateObject(value);
      if (!date) return '';
      const jsDate = date.toDate();
      jsDate.setHours(23, 59, 59, 999);
      try {
        return jsDate.toISOString();
      } catch (error) {
        return '';
      }
    },
    [toDateObject]
  );

  const availabilityDetails = useMemo(() => {
    const startDate = toDateObject(form.availableFrom);
    const endDate = toDateObject(form.availableTo);
    if (!startDate || !endDate) {
      return null;
    }

    const startMs = startDate.toDate().setHours(0, 0, 0, 0);
    const endMs = endDate.toDate().setHours(0, 0, 0, 0);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
      return null;
    }

    const daysActive = Math.floor((endMs - startMs) / DAY_IN_MS) + 1;

    let autoPauseLabel = '';
    if (form.autoPauseAt) {
      const parsed = new Date(form.autoPauseAt);
      if (!Number.isNaN(parsed.getTime())) {
        autoPauseLabel = autoPauseFormatter.format(parsed);
      }
    } else if (form.availableTo) {
      autoPauseLabel = `${formatAvailabilityDate(form.availableTo)} 23:59`;
    }

    return {
      daysActive,
      autoPauseLabel,
    };
  }, [form.availableFrom, form.availableTo, form.autoPauseAt, autoPauseFormatter, formatAvailabilityDate, toDateObject]);


  const applyAvailabilityRange = (startValue, durationValue) => {
    const normalizedDuration = Math.min(Math.max(durationValue || 1, 1), availabilityLimitDays);

    if (!startValue) {
      setForm(prev => ({
        ...prev,
        availableFrom: '',
        availableTo: '',
        autoPauseAt: '',
      }));
      setError('');
      setSuccess('');
      return;
    }

    const parsedStart = toDateObject(startValue);
    if (!parsedStart) {
      setError('Selecciona una fecha de inicio valida.');
      return;
    }

    const endDate = new DateObject(parsedStart).add(normalizedDuration - 1, 'days');
    const startFormatted = parsedStart.format(AVAILABILITY_FORMAT);
    const endFormatted = endDate.format(AVAILABILITY_FORMAT);

    setShowDurationStartHint(false);
    setForm(prev => ({
      ...prev,
      availableFrom: startFormatted,
      availableTo: endFormatted,
      autoPauseAt: buildAutoPauseAt(endFormatted),
    }));
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    const start = toDateObject(form.availableFrom);
    const end = toDateObject(form.availableTo);
    if (start && end) {
      const startMs = start.toDate().setHours(0, 0, 0, 0);
      const endMs = end.toDate().setHours(0, 0, 0, 0);
      if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && startMs <= endMs) {
        const diffDays = Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
        if (diffDays > availabilityLimitDays) {
          const clamped = Math.max(1, availabilityLimitDays);
          if (selectedDuration !== clamped) {
            setSelectedDuration(clamped);
          }
          applyAvailabilityRange(start.format(AVAILABILITY_FORMAT), clamped);
          return;
        }
        if (diffDays !== selectedDuration) {
          setSelectedDuration(diffDays);
        }
        return;
      }
    }
    if (selectedDuration !== 1) {
      setSelectedDuration(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.availableFrom, form.availableTo, availabilityLimitDays, selectedDuration]);

  const handleAvailabilityStartChange = event => {
    const value = event.target.value;
    if (!value) {
      setSelectedDuration(1);
      setShowDurationStartHint(false);
      applyAvailabilityRange('', 1);
      return;
    }
    setShowDurationStartHint(false);
    applyAvailabilityRange(value, selectedDuration || 1);
  };

  const handleDurationSelect = value => {
    const normalized = Math.min(Math.max(value, 1), availabilityLimitDays);
    setSelectedDuration(normalized);
    if (!form.availableFrom) {
      setShowDurationStartHint(true);
      return;
    }
    setShowDurationStartHint(false);
    applyAvailabilityRange(form.availableFrom, normalized);
  };

  const handleAvailabilityClear = () => {
    setForm(prev => ({
      ...prev,
      availableFrom: '',
      availableTo: '',
      autoPauseAt: '',
    }));
    setSelectedDuration(1);
    setShowDurationStartHint(false);
    setError('');
    setSuccess('');
  };

  const handleCreateNew = useCallback(() => {
    clearProductImagePreview();
    const nextForm = createEmptyForm(defaultType);
    setForm(nextForm);
    setFormBaseline(nextForm);
    setSelectedDuration(1);
    setEditingId(null);
    setError('');
    setSuccess('');
    setMode('form');
    setSelectedUnitId('');
    setActiveTab('general');
    setShowUnsavedPrompt(false);
  }, [clearProductImagePreview, defaultType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const intent = params.get('intent');
    if (intent !== 'create_publication') return;
    handleCreateNew();
    params.delete('intent');
    if (!params.get('section')) {
      params.set('section', 'publicaciones');
    }
    const nextSearch = params.toString();
    navigate(`/dashboard?${nextSearch}`, { replace: true });
  }, [location.search, handleCreateNew, navigate]);

  const resetForm = useCallback(() => {
    clearProductImagePreview();
    const nextForm = createEmptyForm(defaultType);
    setForm(nextForm);
    setFormBaseline(nextForm);
    setSelectedDuration(1);
    setEditingId(null);
    setError('');
    setMode('list');
    setSelectedUnitId('');
    setActiveTab('general');
    setShowDurationStartHint(false);
    setShowUnsavedPrompt(false);
  }, [clearProductImagePreview, defaultType]);

  const validatePublication = normalizedDescription => {
    if (!form.title.trim()) {
      setError('El titulo es obligatorio.');
      return false;
    }

    if (form.title.length > TITLE_MAX_LENGTH) {
      setActiveTab('general');
      setError(`El titulo admite hasta ${TITLE_MAX_LENGTH} caracteres.`);
      return false;
    }

    const descriptionLength = richTextToPlainText(normalizedDescription).length;
    if (descriptionLength > DESCRIPTION_MAX_LENGTH) {
      setActiveTab('general');
      setError(`La descripcion admite hasta ${DESCRIPTION_MAX_LENGTH} caracteres.`);
      return false;
    }

    if (!form.location.trim()) {
      setActiveTab('location');
      setError('La ubicacion es obligatoria.');
      return false;
    }

    const resolvedSlaValue =
      form.slaSource === FIELD_SOURCE.UNIT && canUseUnitSla ? unitSlaValue : form.sla;

    if (!resolvedSlaValue.trim()) {
      setActiveTab('conditions');
      setError('Comparte el SLA o tiempos de respuesta del servicio.');
      return false;
    }

    if (form.bannerPreference === 'custom') {
      const hasCustomImage =
        Boolean(form.productImageFile) ||
        (Boolean(form.productImageUrl) && !form.productImageRemoved);
      if (!hasCustomImage) {
        setActiveTab('content');
        setError('Carga una imagen para la publicacion.');
        return false;
      }
    }

    if (shouldCaptureAvailability) {
      const startDate = toDateObject(form.availableFrom);
      const endDate = toDateObject(form.availableTo);

      if (!startDate || !endDate) {
        setError('Selecciona un rango de fechas de disponibilidad.');
        return false;
      }

      const startMs = startDate.toDate().setHours(0, 0, 0, 0);
      const endMs = endDate.toDate().setHours(0, 0, 0, 0);

      if (startMs > endMs) {
        setError('La fecha de inicio debe ser anterior a la fecha final.');
        return false;
      }

      const diffDays = Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
      if (diffDays > availabilityLimitDays) {
        setError(`El plan actual permite publicar hasta ${availabilityLimitLabel}. Ajusta el rango.`);
        return false;
      }
    }

    return true;
  };

  const buildPublicationPayload = (normalizedDescription, statusOverride) => {
    const finalBannerPreference =
      form.bannerPreference === 'unit' && !unitBannerAvailable
        ? 'company'
        : form.bannerPreference;
    const finalBannerUrl =
      finalBannerPreference === 'unit'
        ? selectedUnitPhoto || form.bannerUrl
        : finalBannerPreference === 'custom'
        ? form.productImageUrl || ''
        : '';
    const finalAutoPauseAt =
      shouldCaptureAvailability && form.availableTo ? buildAutoPauseAt(form.availableTo) : '';
    const shouldUseUnitPrice = form.priceSource === FIELD_SOURCE.UNIT && canUseUnitPrice;
    const shouldUseUnitSla = form.slaSource === FIELD_SOURCE.UNIT && canUseUnitSla;
    const finalPrice = shouldUseUnitPrice ? '' : form.price || '';
    const finalPriceLabel = shouldUseUnitPrice ? '' : form.price || '';
    const finalSla = shouldUseUnitSla ? unitSlaValue : form.sla || '';

    return {
      ...form,
      description: normalizedDescription,
      bannerPreference: finalBannerPreference,
      bannerUrl: finalBannerUrl,
      availableFrom: shouldCaptureAvailability ? form.availableFrom || '' : '',
      availableTo: shouldCaptureAvailability ? form.availableTo || '' : '',
      autoPauseAt: shouldCaptureAvailability ? finalAutoPauseAt : '',
      unitAvailabilityRanges: requiresAvailability ? form.unitAvailabilityRanges || [] : [],
      policies: showPolicies ? form.policies : form.policies || '',
      sla: finalSla,
      price: finalPrice,
      priceLabel: finalPriceLabel,
      status: statusOverride,
      company: empresaId || '',
      createdBy: form.createdBy || creatorId || '',
    };
  };

  const savePublication = async ({ status = 'published', skipValidation = false } = {}) => {
    if (!creatorId) {
      setError('No se reconoce al usuario actual.');
      return false;
    }

    const normalizedDescription = normalizeRichTextInput(form.description);

    if (!skipValidation) {
      const isValid = validatePublication(normalizedDescription);
      if (!isValid) {
        return false;
      }
    }

    setSaving(true);
    setError('');

    const payload = buildPublicationPayload(normalizedDescription, status);

    try {
      if (editingId) {
        await updatePublication(editingId, payload);
        setSuccess(status === 'draft' ? 'Borrador actualizado.' : 'Publicacion actualizada.');
      } else {
        await createPublication(payload);
        setSuccess(status === 'draft' ? 'Borrador guardado.' : 'Publicacion creada.');
      }
      await loadPublications();
      resetForm();
      return true;
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la publicacion.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'unitName' ? { unitId: '' } : {}),
    }));
    if (name === 'unitName') {
      setSelectedUnitId('');
    }
    setError('');
  };

  const handleDescriptionChange = useCallback(
    nextValue => {
      const sanitized = normalizeRichTextInput(nextValue);
      setForm(prev => ({
        ...prev,
        description: sanitized,
      }));
      setError('');
    },
    [setError]
  );

  const handleTypeChange = event => {
    const nextType = event.target.value;
    applyTypeChange(nextType);
    setError('');
  };

  const handleProductAvailabilityModeChange = event => {
    const { value } = event.target;
    const nextMode =
      value === PRODUCT_AVAILABILITY_MODES.RANGE
        ? PRODUCT_AVAILABILITY_MODES.RANGE
        : PRODUCT_AVAILABILITY_MODES.ALWAYS;
    setForm(prev => ({
      ...prev,
      availabilityMode: nextMode,
      ...(nextMode === PRODUCT_AVAILABILITY_MODES.ALWAYS
        ? { availableFrom: '', availableTo: '', autoPauseAt: '' }
        : {}),
    }));
    if (nextMode === PRODUCT_AVAILABILITY_MODES.RANGE) {
      if (!form.availableFrom) {
        setShowDurationStartHint(true);
      }
      setActiveTab('availability');
    } else {
      setSelectedDuration(1);
      setShowDurationStartHint(false);
      if (activeTab === 'availability') {
        setActiveTab('general');
      }
    }
    setError('');
    setSuccess('');
  };

  const handleUnitSelect = event => {
    const { value } = event.target;
    setSelectedUnitId(value);
    setForm(prev => {
      return {
        ...prev,
        unitId: value,
      };
    });
    setError('');
  };

  const handlePriceSourceChange = value => {
    setForm(prev => ({
      ...prev,
      priceSource: value,
    }));
    setError('');
  };

  const handleSlaSourceChange = value => {
    setForm(prev => ({
      ...prev,
      slaSource: value,
    }));
    setError('');
  };

  const handleBannerPreferenceChange = event => {
    const { value } = event.target;
    setForm(prev => {
      const nextState = {
        ...prev,
        bannerPreference: value,
      };
      if (value !== 'custom' && (prev.productImageFile || prev.productImageUrl)) {
        clearProductImagePreview();
        nextState.productImageFile = null;
        nextState.productImageUrl = '';
        nextState.productImageRemoved = value !== 'custom' ? true : prev.productImageRemoved;
      }
      return nextState;
    });
    setError('');
  };

  const handleProductImageChange = event => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    clearProductImagePreview();
    if (file) {
      const preview = URL.createObjectURL(file);
      productImagePreviewRef.current = preview;
      setProductImagePreview(preview);
      setForm(prev => ({
        ...prev,
        productImageFile: file,
        productImageUrl: '',
        productImageRemoved: false,
      }));
    } else {
      setForm(prev => ({
        ...prev,
        productImageFile: null,
      }));
    }
  };

  const handleProductImageRemove = () => {
    clearProductImagePreview();
    setForm(prev => ({
      ...prev,
      productImageFile: null,
      productImageUrl: '',
      productImageRemoved: true,
    }));
  };

  useEffect(
    () => () => {
      clearProductImagePreview();
    },
    [clearProductImagePreview]
  );

  const handleFaqChange = (index, field, value) => {
    setForm(prev => {
      const nextFaqs = prev.faqs.map((faq, idx) =>
        idx === index ? { ...faq, [field]: value } : faq
      );
      return {
        ...prev,
        faqs: nextFaqs,
      };
    });
  };

  const handleAddFaq = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, emptyFaq()],
    }));
  };

  const handleRemoveFaq = index => {
    setForm(prev => {
      const nextFaqs = prev.faqs.filter((_, idx) => idx !== index);
      return {
        ...prev,
        faqs: nextFaqs.length ? nextFaqs : [emptyFaq()],
      };
    });
  };

  const handleEdit = useCallback(
    (item, options = {}) => {
      const { focusTab = 'general', resetAvailability = false } = options;
      const normalizedType = normalizePublicationType(item.type);
      const finalType = allowedTypeValues.includes(normalizedType) ? normalizedType : defaultType;
      const isService = isServicePublication(finalType);
      const hasLinkedUnitForItem = isService ? Boolean(item.unitId) : false;
      const hasStoredAvailabilityRange = Boolean(item.availableFrom && item.availableTo);
      const shouldRestoreAvailability = !resetAvailability && (isService || hasStoredAvailabilityRange);
      const nextAvailabilityMode = isService
        ? PRODUCT_AVAILABILITY_MODES.RANGE
        : hasStoredAvailabilityRange && !resetAvailability
        ? PRODUCT_AVAILABILITY_MODES.RANGE
        : PRODUCT_AVAILABILITY_MODES.ALWAYS;
      clearProductImagePreview();
      const nextForm = {
        title: item.title || '',
        description: ensureRichText(item.description),
        type: finalType,
        unitName: item.unitName || '',
        unitId: isService ? item.unitId || '' : '',
        unitAvailabilityRanges: isService ? item.unitAvailabilityRanges || [] : [],
        location: item.location || '',
        availableFrom: shouldRestoreAvailability ? item.availableFrom || '' : '',
        availableTo: shouldRestoreAvailability ? item.availableTo || '' : '',
        autoPauseAt: shouldRestoreAvailability ? item.autoPauseAt || '' : '',
        faqs: item.faqs?.length ? item.faqs : [emptyFaq()],
        policies: item.policies || '',
        sla: item.sla || '',
        price: hidesPriceForType(finalType) ? '' : item.customPrice || '',
        priceSource:
          !hasLinkedUnitForItem || hidesPriceForType(finalType)
            ? FIELD_SOURCE.CUSTOM
            : item.customPrice
            ? FIELD_SOURCE.CUSTOM
            : FIELD_SOURCE.UNIT,
        slaSource: FIELD_SOURCE.CUSTOM,
        status: item.status || 'published',
        bannerPreference: item.bannerPreference || 'company',
        bannerUrl: item.bannerUrl || '',
        productImageFile: null,
        productImageUrl: item.productImageUrl || '',
        productImageRemoved: false,
        createdBy: item.createdBy || item.createdByUser?.id || '',
        availabilityMode: nextAvailabilityMode,
      };
      setForm(nextForm);
      setFormBaseline(nextForm);
      if (resetAvailability) {
        setSelectedDuration(1);
        setShowDurationStartHint(true);
      } else {
        setShowDurationStartHint(false);
      }
      setEditingId(item.id);
      setError('');
      setSuccess('');
      setMode('form');
      setActiveTab(focusTab);
      setSelectedUnitId(isService ? item.unitId || '' : '');
    },
    [
      allowedTypeValues,
      clearProductImagePreview,
      defaultType,
      setForm,
      setSelectedDuration,
      setShowDurationStartHint,
      setEditingId,
      setError,
      setSuccess,
      setMode,
      setActiveTab,
      setSelectedUnitId,
      setFormBaseline,
    ]
  );

  const handleDelete = async id => {
    setStatusMenuOpen('');
    if (!window.confirm('Eliminar esta publicacion?')) return;
    setSaving(true);
    setError('');
    try {
      await deletePublication(id);
      await loadPublications();
      setSuccess('Publicacion eliminada.');
      if (editingId === id) {
        resetForm();
        setMode('list');
      }
    } catch (err) {
      const message = err?.message || '';
      if (
        message.includes('Failed to delete record') &&
        message.includes('required relation')
      ) {
        setError(
          'No se puede eliminar la publicacion porque tiene una reservacion activa. Termina la reservacion antes de intentarlo.'
        );
      } else {
        setError(message || 'No se pudo eliminar la publicacion.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePromotion = useCallback(
    async item => {
      if (!item?.id) return;
      setStatusMenuOpen('');
      const resourceId = item.id;
      const alreadyPromoted = isPromoted('publication', resourceId);
      setError('');
      setSuccess('');
      setPromotionLoadingFor(resourceId, true);
      try {
        if (alreadyPromoted) {
          await unpromoteResource('publication', resourceId);
          setSuccess('Promocion eliminada.');
        } else {
          await promoteResource('publication', resourceId);
          setSuccess('Publicacion promocionada.');
        }
        await refreshPromotions();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('flotte:promotions-updated'));
        }
      } catch (err) {
        setError(err?.message || 'No fue posible actualizar la promocion.');
      } finally {
        setPromotionLoadingFor(resourceId, false);
      }
    },
    [isPromoted, refreshPromotions, setPromotionLoadingFor]
  );

  const handleRenew = useCallback(
    item => {
      if (!item) return;
      setStatusMenuOpen('');
      const normalizedType = normalizePublicationType(item.type);
      const focusTab =
        isServicePublication(normalizedType) && normalizedType !== 'service_road_assistance'
          ? 'availability'
          : 'general';
      handleEdit(item, { focusTab, resetAvailability: true });
    },
    [handleEdit]
  );

  const handleStatusSelect = useCallback(
    async (item, nextStatus) => {
      if (!item?.id || !nextStatus || item.status === nextStatus) {
        setStatusMenuOpen('');
        return;
      }
      setError('');
      setSuccess('');
      setStatusUpdatingFor(item.id, true);
      try {
        const updated = await setPublicationStatus(item.id, nextStatus);
        setList(prev =>
          prev.map(entry =>
            entry.id === item.id ? { ...entry, status: updated.status || nextStatus } : entry
          )
        );
        setSuccess('Estado actualizado.');
      } catch (err) {
        setError(err?.message || 'No se pudo actualizar el estado.');
      } finally {
        setStatusUpdatingFor(item.id, false);
        setStatusMenuOpen('');
      }
    },
    [setError, setList, setStatusMenuOpen, setStatusUpdatingFor, setSuccess]
  );

  const handleSubmit = async event => {
    event.preventDefault();
    await savePublication({ status: 'published' });
  };

  const handleBackToSummary = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedPrompt(true);
      return;
    }
    resetForm();
  }, [hasUnsavedChanges, resetForm]);

  const handleDiscardChanges = () => {
    resetForm();
  };

  const handleSaveDraftFromPrompt = async () => {
    const saved = await savePublication({ status: 'draft', skipValidation: true });
    if (saved) {
      setShowUnsavedPrompt(false);
    }
  };

  const handleStayOnForm = () => {
    setShowUnsavedPrompt(false);
  };

  const publicationStats = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) {
      return {
        total: 0,
        published: 0,
        draft: 0,
        paused: 0,
        totalViews: 0,
        averageViews: 0,
        lastUpdated: '',
      };
    }

    let published = 0;
    let draft = 0;
    let paused = 0;
    let totalViews = 0;
    let latest = null;

    list.forEach(item => {
      const status = String(item.status || '').toLowerCase();
      if (status === 'published' || status === 'active') {
        published += 1;
      } else if (status === 'draft') {
        draft += 1;
      } else if (status === 'archived' || status === 'paused') {
        paused += 1;
      }

      const overrideViews = viewCounts?.[item.id];
      const views =
        typeof overrideViews === 'number' && Number.isFinite(overrideViews)
          ? overrideViews
          : Number(item.views ?? item.viewCount ?? 0);
      if (Number.isFinite(views)) {
        totalViews += views;
      }

      const updatedAt = item.updated || item.created;
      if (updatedAt) {
        const parsed = new Date(updatedAt);
        if (!Number.isNaN(parsed.getTime())) {
          if (!latest || parsed > latest) {
            latest = parsed;
          }
        }
      }
    });

    const averageViews = list.length ? Math.round(totalViews / list.length) : 0;
    const lastUpdated =
      latest && !Number.isNaN(latest.getTime()) ? dateFormatter.format(latest) : '';

  return {
    total: list.length,
    published,
    draft,
    paused,
    totalViews,
    averageViews,
    lastUpdated,
  };
}, [dateFormatter, list, viewCounts]);

  const publicationKpis = useMemo(() => {
    const active = Math.max(0, Number(publicationStats.published) || 0);
    const paused = Math.max(0, Number(publicationStats.paused) || 0);
    const totalViews = Math.max(0, Number(publicationStats.totalViews) || 0);
    const total = Math.max(0, Number(publicationStats.total) || 0);

    const kpiValue = value => viewFormatter.format(value);

    return [
      {
        id: 'active',
        label: 'Publicaciones Activas',
        value: kpiValue(active),
        context: active > 0 ? 'Listadas en marketplace' : 'Sin publicaciones visibles',
        icon: FiCheckCircle,
        variant: active > 0 ? 'success' : 'muted',
      },
      {
        id: 'paused',
        label: 'Pausadas',
        value: kpiValue(paused),
        context: paused > 0 ? 'Pendientes de ajustes' : 'Todo al dia',
        icon: FiPauseCircle,
        variant: paused > 0 ? 'warning' : 'muted',
      },
      {
        id: 'views',
        label: 'Visitas Totales',
        value: kpiValue(totalViews),
        context: 'Visitas acumuladas',
        icon: FiEye,
        variant: 'info',
      },
      {
        id: 'total',
        label: 'Total Publicado',
        value: kpiValue(total),
        context: 'Publicaciones registradas',
        icon: FiLayers,
        variant: total > 0 ? 'default' : 'muted',
      },
    ];
  }, [publicationStats, viewFormatter]);

  const renderListView = () => (
    <div className="publication-list-layout">
      <section className="publication-list-view">
        {success && <div className="form-success">{success}</div>}
        {mode === 'list' && error && <div className="form-error">{error}</div>}

        <div className={`dashboard-kpi-wrapper${kpiCollapsed ? ' is-collapsed' : ''}`}>
          <div className="dashboard-kpi-wrapper__controls">
            <span className="dashboard-kpi-wrapper__label">Indicadores</span>
            <button
              type="button"
              className="dashboard-kpi-toggle"
              onClick={() => setKpiCollapsed(prev => !prev)}
              aria-pressed={!kpiCollapsed}
            >
              {kpiCollapsed ? 'Mostrar' : 'Ocultar'}
            </button>
          </div>
          {!kpiCollapsed ? (
            <div className="publication-kpi-grid">
              {publicationKpis.map(card => {
                const Icon = card.icon;
                return (
                  <article key={card.id} className="publication-kpi-card">
                    <span
                      className={`publication-kpi-card__icon publication-kpi-card__icon--${card.variant}`}
                      aria-hidden="true"
                    >
                      <Icon />
                    </span>
                    <div className="publication-kpi-card__content">
                      <p className="publication-kpi-card__label">{card.label}</p>
                      <strong
                        className={`publication-kpi-card__value publication-kpi-card__value--${card.variant}`}
                      >
                        {card.value}
                      </strong>
                      <span className="publication-kpi-card__context">{card.context}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="publication-table-card">
          <div className="publication-toolbar">
            <div className="publication-toolbar__context">
              <h2 className="publication-toolbar__title">Todas las Publicaciones</h2>
              <div className="publication-toolbar__search">
                <span className="publication-toolbar__search-icon" aria-hidden="true">
                  <FiSearch />
                </span>
                <label htmlFor={publicationSearchInputId} className="visually-hidden">
                  Buscar publicaciones
                </label>
                <input
                  id={publicationSearchInputId}
                  type="text"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Buscar por título..."
                />
              </div>
            </div>
            <div className="publication-toolbar__actions">
              <button
                type="button"
                className="publication-toolbar__button"
                onClick={handleCreateNew}
              >
                <FiPlus aria-hidden="true" />
                Crear publicacion
              </button>
            </div>
          </div>

          <div className="publication-table-card__body">
            <div className="table-scroll-hint" role="note">
              <span aria-hidden="true" className="table-scroll-hint__icon" />
              <span className="table-scroll-hint__text">
                Desliza horizontalmente, haz clic en un encabezado para ordenar (A-Z/Z-A), clic derecho para
                configurar columnas o arrastra el borde para ajustar el ancho.
              </span>
            </div>

            <div className="publication-table-wrapper">
              <table className="publication-table">
                <thead>
                  <tr>
                    {visiblePublicationColumns.map(column => {
                      const isSorted = sortConfig.key === column.key;
                      const ariaSort = isSorted
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none';
                      return (
                        <th
                          key={column.key}
                          ref={element => {
                            if (element) {
                              columnHeaderRefs.current[column.key] = element;
                            }
                          }}
                          style={{ minWidth: `${column.width}px`, width: `${column.width}px` }}
                          className="table-column-header"
                          onClick={() => handleColumnSort(column.key)}
                          onContextMenu={event => handleColumnContextMenu(event, column.key)}
                          onKeyDown={event => handleColumnHeaderKeyDown(event, column.key)}
                          tabIndex={0}
                          aria-sort={ariaSort}
                          title="Haz clic para ordenar (A-Z/Z-A). Clic derecho abre la configuracion de columnas."
                        >
                          <span className="table-column-header__label">
                            {column.label}
                            <span
                              className={`table-column-header__sort${
                                isSorted ? ` table-column-header__sort--${sortConfig.direction}` : ''
                              }`}
                              aria-hidden="true"
                            >
                              {isSorted ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                            </span>
                            {isSorted ? (
                              <span className="visually-hidden">
                                {`Ordenado ${
                                  sortConfig.direction === 'asc' ? 'ascendente' : 'descendente'
                                }`}
                              </span>
                            ) : null}
                          </span>
                          <span
                            role="presentation"
                            className="table-column-header__resizer"
                            onMouseDown={event => handleColumnResizeMouseDown(event, column)}
                            onTouchStart={event => handleColumnResizeTouchStart(event, column)}
                            aria-hidden="true"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, index) =>
                      renderPlaceholderRow(`loading-${index}`)
                    )
                  ) : paginatedList.length ? (
                    paginatedList.map(item => {
                      const rawViews = Number(item.views ?? item.viewCount ?? 0);
                      const overrideViews = viewCounts?.[item.id];
                      const normalizedViews =
                        typeof overrideViews === 'number' && Number.isFinite(overrideViews)
                          ? Math.max(0, Math.round(overrideViews))
                          : Number.isFinite(rawViews)
                          ? Math.max(0, Math.round(rawViews))
                          : 0;
                      const viewLabel = viewFormatter.format(normalizedViews);
                      const itemLabel = item.title || item.unitName || 'Publicacion';
                      const promoted = isPromoted('publication', item.id);
                      const promotionBusy = Boolean(promotionLoading[item.id]);
                      const normalizedStatus = String(item.status || '').toLowerCase();
                      const statusLabel = STATUS_LABELS[normalizedStatus] || item.status || 'Sin estado';
                      const rowActions = [];
                      const creatorLabel =
                        item.createdByUser?.name ||
                        item.createdByUser?.username ||
                        item.createdByUser?.email ||
                        'Sin registrar';
                      const updaterLabel =
                        item.updatedByUser?.name ||
                        item.updatedByUser?.username ||
                        item.updatedByUser?.email ||
                        creatorLabel;

                      if (normalizedStatus === 'paused') {
                        rowActions.push({
                          key: 'renew',
                          label: 'Renovar disponibilidad',
                          onSelect: () => handleRenew(item),
                        });
                        rowActions.push({
                          key: 'archive',
                          label: 'Archivar',
                          onSelect: () => handleStatusSelect(item, 'archived'),
                        });
                      } else if (normalizedStatus === 'archived') {
                        rowActions.push({
                          key: 'renew',
                          label: 'Renovar y publicar',
                          onSelect: () => handleRenew(item),
                        });
                      } else if (normalizedStatus === 'published') {
                        rowActions.push({
                          key: 'archive',
                          label: 'Archivar',
                          onSelect: () => handleStatusSelect(item, 'archived'),
                        });
                      } else {
                        rowActions.push({
                          key: 'publish',
                          label: 'Publicar',
                          onSelect: () => handleRenew(item),
                        });
                        rowActions.push({
                          key: 'archive',
                          label: 'Archivar',
                          onSelect: () => handleStatusSelect(item, 'archived'),
                        });
                      }

                      rowActions.push({
                        key: 'toggle-promotion',
                        label: promotionBusy
                          ? 'Guardando...'
                          : promoted
                          ? 'Quitar promocion'
                          : 'Promocionar',
                        onSelect: () => handleTogglePromotion(item),
                        disabled: promotionBusy || saving,
                      });
                      rowActions.push({
                        key: 'delete',
                        label: 'Eliminar publicacion',
                        onSelect: () => handleDelete(item.id),
                      });
                      const hasStatusActions = rowActions.length > 0;

                      const columnContent = {
                        info: (
                          <div className="publication-table-main">
                            <strong>{itemLabel}</strong>
                            <p>{item.location || item.unitName || 'Sin descripcion'}</p>
                          </div>
                        ),
                        status: (
                          <div
                            className="publication-status-control"
                            ref={statusMenuOpen === item.id ? statusMenuRef : undefined}
                          >
                            <button
                              type="button"
                              className={`publication-status publication-status-button status-${normalizedStatus}`}
                              onClick={event => {
                                event.stopPropagation();
                                if (!hasStatusActions || statusUpdating[item.id] || saving) return;
                                setStatusMenuOpen(prev => (prev === item.id ? '' : item.id));
                              }}
                              disabled={!hasStatusActions || Boolean(statusUpdating[item.id] || saving)}
                              aria-haspopup={hasStatusActions ? 'menu' : undefined}
                              aria-expanded={hasStatusActions && statusMenuOpen === item.id}
                              aria-controls={
                                hasStatusActions && statusMenuOpen === item.id
                                  ? `pub-status-menu-${item.id}`
                                  : undefined
                              }
                              title={hasStatusActions ? 'Gestionar disponibilidad' : 'Estado actual'}
                            >
                              {statusUpdating[item.id] ? 'Guardando...' : statusLabel}
                            </button>
                            {hasStatusActions && statusMenuOpen === item.id ? (
                              <div
                                id={`pub-status-menu-${item.id}`}
                                className="publication-status-menu"
                                role="menu"
                                onClick={event => event.stopPropagation()}
                              >
                                {rowActions.map(action => (
                                  <button
                                    key={action.key}
                                    type="button"
                                    role="menuitem"
                                    className="publication-status-option"
                                    onClick={event => {
                                      event.stopPropagation();
                                      action.onSelect();
                                    }}
                                    disabled={
                                      Boolean(statusUpdating[item.id]) || Boolean(action.disabled)
                                    }
                                  >
                                    {action.label}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ),
                        views: (
                          <span className="publication-views">
                            <FiEye aria-hidden="true" />
                            <span>{viewLabel}</span>
                          </span>
                        ),
                        createdBy: creatorLabel,
                        updatedBy: updaterLabel,
                        updatedAt: (
                          <time dateTime={item.updated}>
                            {new Date(item.updated || item.created).toLocaleDateString()}
                          </time>
                        ),
                      };

                      return (
                        <tr
                          key={item.id}
                          className="publication-table-row"
                          data-clickable="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => handleEdit(item)}
                          onKeyDown={event => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleEdit(item);
                            }
                          }}
                        >
                          {visiblePublicationColumns.map(column => (
                            <td
                              key={`${item.id}-${column.key}`}
                              style={{
                                minWidth: `${column.width}px`,
                                width: `${column.width}px`,
                              }}
                            >
                              {columnContent[column.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : showSearchNoResults ? (
                    <tr className="publication-table-empty">
                      <td colSpan={Math.max(1, visiblePublicationColumns.length)}>
                        <p>
                          No encontramos publicaciones que coincidan con "{searchQuery.trim()}".
                        </p>
                      </td>
                    </tr>
                  ) : showEmptyState ? (
                    <tr className="publication-table-empty">
                      <td colSpan={Math.max(1, visiblePublicationColumns.length)}>
                        <FaStickyNote aria-hidden="true" />
                        <p>Crea tu primera publicacion para mostrarla en Flotte.</p>
                      </td>
                    </tr>
                  ) : (
                    <tr className="publication-table-empty">
                      <td colSpan={Math.max(1, visiblePublicationColumns.length)}>
                        <p>Sin resultados en esta pagina.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="publication-table-card__footer">
            <div className="table-pagination">
              <div className="table-pagination__page-size">
                <label>
                  Mostrar
                  <select value={pageSize} onChange={handlePageSizeChange}>
                    {PAGE_SIZE_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  publicaciones
                </label>
              </div>
              <div
                className="table-pagination__controls"
                role="navigation"
                aria-label="Paginacion de publicaciones"
              >
                <button
                  type="button"
                  onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                  disabled={page === 0}
                >
                  Anterior
                </button>
                <div className="table-pagination__pages">
                  {pageNumbers.map(number => (
                    <button
                      type="button"
                      key={`publication-page-${number}`}
                      className={number === page ? 'active' : ''}
                      aria-current={number === page ? 'page' : undefined}
                      onClick={() => setPage(number)}
                    >
                      {number + 1}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
                  disabled={page >= totalPages - 1}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderFormView = () => {
    const normalizedType = normalizePublicationType(form.type);
    const isProductType = isProductPublication(normalizedType);
    const isServiceType = isServicePublication(normalizedType);
    const showPriceTab = !hidesPriceForType(normalizedType) || !hasLinkedUnit;
    const unitReferenceLabel = isProductType
      ? 'Referencia interna del producto'
      : 'Nombre interno del servicio';
    const titleLength = form.title.length;
    const titleLimitExceeded = titleLength > TITLE_MAX_LENGTH;
    const descriptionLength = richTextToPlainText(form.description).length;
    const descriptionLimitExceeded = descriptionLength > DESCRIPTION_MAX_LENGTH;
    const mediaPreview = productImagePreview || form.productImageUrl;
    const normalizedPriceSource =
      form.priceSource === FIELD_SOURCE.UNIT && canUseUnitPrice
        ? FIELD_SOURCE.UNIT
        : FIELD_SOURCE.CUSTOM;
    const normalizedSlaSource =
      form.slaSource === FIELD_SOURCE.UNIT && canUseUnitSla
        ? FIELD_SOURCE.UNIT
        : FIELD_SOURCE.CUSTOM;
    const priceInputValue =
      normalizedPriceSource === FIELD_SOURCE.UNIT ? unitPriceLabel : form.price;
    const slaFieldValue =
      normalizedSlaSource === FIELD_SOURCE.UNIT ? unitSlaValue : form.sla;

    const generalTabContent = (
      <article className="publication-card">
        <header className="publication-card-head">
          <h3>Detalles principales</h3>
          <p>Define que publicaras, para quien y como lo describiras.</p>
        </header>
        <div className="publication-general-grid">
          <div className="publication-field publication-field--type">
            <label id="pub-type-label">
              Tipo <span className="required-indicator" aria-hidden="true">*</span>
            </label>
            <div className="publication-type-grid" role="radiogroup" aria-labelledby="pub-type-label">
              {visibleTypeOptions.map(option => {
                const normalizedOptionValue = normalizePublicationType(option.value);
                const meta = PUBLICATION_TYPE_META[normalizedOptionValue] || {};
                const IconComponent = meta.icon || FiTag;
                const isSelected = normalizedType === normalizedOptionValue;
                return (
                  <label
                    key={option.value}
                    className={`publication-type-chip${isSelected ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={option.value}
                      checked={isSelected}
                      onChange={handleTypeChange}
                      disabled={visibleTypeOptions.length <= 1}
                    />
                    <span className="publication-type-chip__icon" aria-hidden="true">
                      <IconComponent size={16} />
                    </span>
                    <span className="publication-type-chip__label">
                      {meta.title || option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="publication-field-hint">
              Esto ayuda a mostrar tu publicacion en la categoria correcta.
            </p>
          </div>
          <div className="publication-field publication-field--title">
            <label htmlFor="pub-title">
              Titulo <span className="required-indicator" aria-hidden="true">*</span>
            </label>
            <input
              id="pub-title"
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              placeholder="Ej. Custodia para flotilla norte"
              required
            />
            <p className="publication-field-hint">
              Usa un titulo claro que resuma tu servicio, unidad o producto.
            </p>
            <p
              className={`publication-field-counter${titleLimitExceeded ? ' is-warning' : ''}`}
              aria-live="polite"
            >
              {titleLength}/{TITLE_MAX_LENGTH} caracteres
            </p>
            {titleLimitExceeded ? (
              <p className="publication-field-warning">
                El titulo admite hasta {TITLE_MAX_LENGTH} caracteres. Ajustalo para continuar.
              </p>
            ) : null}
          </div>
          <div className="publication-field publication-field--description">
            <label id="pub-description-label" htmlFor="pub-description">
              Descripcion <span className="required-indicator" aria-hidden="true">*</span>
            </label>
            <RichTextEditor
              id="pub-description"
              value={form.description}
              onChange={handleDescriptionChange}
              ariaLabelledby="pub-description-label"
              ariaDescribedby="pub-description-help"
              placeholder="Describe el alcance, condiciones y beneficios de tu oferta."
              className="publication-rich-editor"
            />
            <p id="pub-description-help" className="publication-field-hint">
              Agrega saltos de linea, listas o negritas para resaltar la informacion clave que tus clientes necesitan conocer.
            </p>
            <p
              className={`publication-field-counter${
                descriptionLimitExceeded ? ' is-warning' : ''
              }`}
              aria-live="polite"
            >
              {descriptionLength}/{DESCRIPTION_MAX_LENGTH} caracteres
            </p>
            {descriptionLimitExceeded ? (
              <p className="publication-field-warning">
                La descripcion admite hasta {DESCRIPTION_MAX_LENGTH} caracteres. Ajustala para continuar.
              </p>
            ) : null}
          </div>
          <div className="publication-field publication-field--unit">
            <div className="publication-field-header">
              <label htmlFor="pub-unit">{unitReferenceLabel}</label>
              <button
                type="button"
                className="publication-field-tooltip"
                title={UNIT_REFERENCE_TOOLTIP}
                aria-label={UNIT_REFERENCE_TOOLTIP}
              >
                <FiInfo aria-hidden="true" />
              </button>
            </div>
            <input
              id="pub-unit"
              name="unitName"
              type="text"
              value={form.unitName}
              onChange={handleChange}
              placeholder={
                isProductType
                  ? 'Nombre o referencia del producto'
                  : 'Nombre o referencia del servicio'
              }
            />
            <span className="publication-unit-hint">
              {isProductType
                ? 'Escribe la referencia o nombre comercial del producto.'
                : 'Comparte el nombre interno del servicio para identificarlo en tu catalogo.'}
            </span>
          </div>
          {isServiceType ? (
            <div className="publication-field publication-field--unit-link">
              <label htmlFor="pub-unit-select">Vincula una unidad registrada (opcional)</label>
              {unitOptions.length > 0 ? (
                <select
                  id="pub-unit-select"
                  className="publication-unit-select"
                  value={selectedUnitId}
                  onChange={handleUnitSelect}
                >
                  <option value="">Selecciona una unidad registrada</option>
                  {unitOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="publication-unit-select placeholder">
                  <span>Sin unidades registradas</span>
                </div>
              )}
              <span className="publication-unit-hint">
                Vincula una unidad de tu catalogo para reutilizar su disponibilidad e imagen.
              </span>
            </div>
          ) : null}
          {isProductType ? (
            <div className="publication-field full publication-availability-mode">
              <span className="publication-availability-mode__title">Disponibilidad mostrada</span>
              <p className="publication-field-hint">
                Elige como se mostrara tu calendario en la ficha publica.
              </p>
              <div
                className="publication-availability-mode__options"
                role="radiogroup"
                aria-label="Disponibilidad para clientes"
              >
                <label
                  className={`publication-availability-mode__option${
                    productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.ALWAYS ? ' is-active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="productAvailabilityMode"
                    value={PRODUCT_AVAILABILITY_MODES.ALWAYS}
                    checked={productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.ALWAYS}
                    onChange={handleProductAvailabilityModeChange}
                  />
                  <div>
                    <strong>Siempre disponible</strong>
                    <span>Mostraremos el calendario como disponible todos los dias.</span>
                  </div>
                </label>
                <label
                  className={`publication-availability-mode__option${
                    productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.RANGE ? ' is-active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="productAvailabilityMode"
                    value={PRODUCT_AVAILABILITY_MODES.RANGE}
                    checked={productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.RANGE}
                    onChange={handleProductAvailabilityModeChange}
                  />
                  <div>
                    <strong>Solo durante un periodo</strong>
                    <span>Define un rango en la pestaña "Agenda" para mostrar los dias activos.</span>
                  </div>
                </label>
              </div>
              {productAvailabilityMode === PRODUCT_AVAILABILITY_MODES.RANGE && !form.availableFrom ? (
                <p className="publication-field-hint publication-availability-mode__reminder" role="alert">
                  Selecciona la pestaña "Agenda" para elegir el rango.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </article>
    );

    const contentTabContent = (
      <article className="publication-card">
        <header className="publication-card-head">
          <h3>
            Imagen destacada <span className="required-indicator" aria-hidden="true">*</span>
          </h3>
          <p>Elige si usaras tu banner corporativo, una foto de unidad o una imagen nueva.</p>
        </header>
        <div className="publication-field full publication-banner-settings">
          <span className="publication-banner-title">Fuente de imagen</span>
          <div className="publication-banner-options" role="radiogroup" aria-label="Seleccionar estilo de banner">
            <label
              className={`publication-banner-option${form.bannerPreference === 'company' ? ' is-active' : ''}`}
            >
              <input
                type="radio"
                name="bannerPreference"
                value="company"
                checked={form.bannerPreference === 'company'}
                onChange={handleBannerPreferenceChange}
              />
              <div>
                <strong>Imagen corporativa</strong>
                <span>Usa el degradado o banner configurado en tu empresa.</span>
              </div>
            </label>
            <label
              className={`publication-banner-option${form.bannerPreference === 'custom' ? ' is-active' : ''}`}
            >
              <input
                type="radio"
                name="bannerPreference"
                value="custom"
                checked={form.bannerPreference === 'custom'}
                onChange={handleBannerPreferenceChange}
              />
              <div>
                <strong>Imagen nueva</strong>
                <span>Sube un archivo especifico para esta publicacion.</span>
              </div>
            </label>
            <label
              className={`publication-banner-option${form.bannerPreference === 'unit' ? ' is-active' : ''}${
                !unitBannerAvailable ? ' is-disabled' : ''
              }`}
            >
              <input
                type="radio"
                name="bannerPreference"
                value="unit"
                checked={form.bannerPreference === 'unit'}
                onChange={handleBannerPreferenceChange}
                disabled={!unitBannerAvailable}
              />
              <div>
                <strong>Foto de la unidad</strong>
                <span>Usa la fotografia principal de la unidad vinculada.</span>
              </div>
            </label>
          </div>
          {form.bannerPreference === 'unit' && !unitBannerAvailable ? (
            <p className="publication-field-warning">
              La unidad seleccionada no cuenta con una imagen. Cargala en el catalogo de unidades para activar esta opcion.
            </p>
          ) : null}
        </div>
        {form.bannerPreference === 'custom' ? (
          <div className="publication-media">
            <label htmlFor="pub-product-image" className="publication-media__input">
              <span>Selecciona una imagen</span>
              <input
                id="pub-product-image"
                type="file"
                accept="image/*"
                onChange={handleProductImageChange}
              />
            </label>
            {mediaPreview ? (
              <div className="publication-media__preview">
                <img src={mediaPreview} alt="Previsualizacion de la publicacion" />
                <button type="button" className="btn btn-secondary" onClick={handleProductImageRemove}>
                  Quitar imagen
                </button>
              </div>
            ) : (
              <p className="publication-media__hint">
                Formatos compatibles: JPG, PNG o WebP. Tamanio sugerido 1600x900 px.
              </p>
            )}
          </div>
        ) : (
          <p className="publication-field-hint">
            Selecciona "Imagen nueva" para cargar un archivo diferente.
          </p>
        )}
      </article>
    );

    const availabilityTabContent = shouldCaptureAvailability ? (
      <article className="publication-card publication-card--availability">
        <div className="publication-availability">
          <header className="publication-availability__header">
            <span className="publication-availability__icon" aria-hidden="true">
              <FaCalendarAlt />
            </span>
            <div className="publication-availability__info">
              <h3>{requiresAvailability ? 'Agenda operativa' : 'Ventana visible'}</h3>
              <p>
                Selecciona los dias consecutivos en que la publicacion estara activa. Tu plan
                permite publicar hasta {availabilityLimitLabel} por periodo.
              </p>
            </div>
            <div className="publication-availability__actions">
              {form.availableFrom && form.availableTo ? (
                <button
                  type="button"
                  className="btn-action"
                  onClick={handleAvailabilityClear}
                  disabled={saving}
                >
                  Limpiar
                </button>
              ) : null}
            </div>
          </header>

          <div className="publication-availability__summary" aria-live="polite">
            {form.availableFrom && form.availableTo ? (
              <div className="publication-availability__range">
                <p>
                  Del <strong>{formatAvailabilityDate(form.availableFrom)}</strong> al{' '}
                  <strong>{formatAvailabilityDate(form.availableTo)}</strong>
                </p>
                {availabilityDetails ? (
                  <ul className="publication-availability__insights">
                    <li>
                      {availabilityDetails.daysActive === 1
                        ? 'Se mostrará 1 día completo.'
                        : `Se mostrará durante ${availabilityDetails.daysActive} días.`}
                    </li>
                    {availabilityDetails.autoPauseLabel ? (
                      <li>Se pausará automáticamente el {availabilityDetails.autoPauseLabel}.</li>
                    ) : null}
                    <li>Máximo permitido por tu plan: {availabilityLimitLabel}.</li>
                  </ul>
                ) : (
                  <p>Máximo permitido por tu plan: {availabilityLimitLabel}.</p>
                )}
              </div>
            ) : (
              <span className="publication-availability__empty">
                Selecciona un rango para definir la disponibilidad.
              </span>
            )}
          </div>

          <div className="publication-availability__controls">
            <div className="publication-availability__start">
              <label htmlFor="publication-availability-start">Fecha de inicio</label>
              <input
                id="publication-availability-start"
                type="date"
                value={form.availableFrom}
                onChange={handleAvailabilityStartChange}
                disabled={saving}
              />
              <small>Selecciona el primer día en que tu publicación estará activa.</small>
            </div>
            <div className="publication-availability__durations">
              <span className="publication-availability__durations-label">Duración</span>
              {showDurationStartHint && !form.availableFrom ? (
                <p className="publication-availability__hint" role="alert">
                  Selecciona primero la fecha de inicio para elegir la duración.
                </p>
              ) : null}
              <div
                className="publication-availability__duration-options"
                role="group"
                aria-label="Duración de la publicación"
              >
                {durationOptions.map(option => (
                  <button
                    type="button"
                    key={`availability-duration-${option}`}
                    className={`publication-availability__duration-button${
                      selectedDuration === option ? ' is-active' : ''
                    }`}
                    onClick={() => handleDurationSelect(option)}
                    disabled={saving}
                  >
                    {option === 1 ? '1 día' : `${option} días`}
                  </button>
                ))}
              </div>
              <small>Las publicaciones se muestran en días consecutivos.</small>
            </div>
          </div>
        </div>
      </article>
    ) : (
      <article className="publication-card">
        <header className="publication-card-head">
          <h3>Agenda operativa</h3>
          <p>Este tipo de publicacion se muestra como disponible permanentemente.</p>
        </header>
        <p className="publication-field-hint">
          Si necesitas restringir fechas, cambia el modo de disponibilidad en la seccion “Detalles principales” o pausa la publicacion desde el listado.
        </p>
      </article>
    );

    const conditionsTabContent = (
      <article className="publication-card">
        <header className="publication-card-head">
          <h3>Precio y nivel de servicio</h3>
          <p>Comparte tarifas de referencia y tus compromisos operativos.</p>
        </header>
        {showPriceTab ? (
          <>
            {hasLinkedUnit ? (
              <div
                className="publication-field-source"
                role="radiogroup"
                aria-label="Fuente del precio"
              >
                <div className="publication-field-source__header">
                  <span>Fuente del precio</span>
                  <button
                    type="button"
                    className="publication-field-tooltip"
                    title="Puedes fijar tarifas por destino desde la unidad. En la publicación solo mostramos precios fijos o de referencia."
                    aria-label="Puedes fijar tarifas por destino desde la unidad. En la publicación solo mostramos precios fijos o de referencia."
                  >
                    <FiInfo aria-hidden="true" />
                  </button>
                </div>
                <div className="publication-field-source__options">
                  <label
                    className={`publication-field-source__option${
                      normalizedPriceSource === FIELD_SOURCE.UNIT ? ' is-active' : ''
                    }${!canUseUnitPrice ? ' is-disabled' : ''}`}
                  >
                    <input
                      type="radio"
                      name="price-source"
                      value={FIELD_SOURCE.UNIT}
                      checked={normalizedPriceSource === FIELD_SOURCE.UNIT}
                      onChange={() => handlePriceSourceChange(FIELD_SOURCE.UNIT)}
                      disabled={!canUseUnitPrice}
                    />
                    <div>
                      <strong>Usar precio de la unidad</strong>
                      <span>
                        {canUseUnitPrice
                          ? unitPriceLabel
                          : 'Agrega una tarifa en la unidad para habilitar esta opción.'}
                      </span>
                    </div>
                  </label>
                  <label
                    className={`publication-field-source__option${
                      normalizedPriceSource === FIELD_SOURCE.CUSTOM ? ' is-active' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="price-source"
                      value={FIELD_SOURCE.CUSTOM}
                      checked={normalizedPriceSource === FIELD_SOURCE.CUSTOM}
                      onChange={() => handlePriceSourceChange(FIELD_SOURCE.CUSTOM)}
                    />
                    <div>
                      <strong>Fijar precio en la publicación</strong>
                      <span>Comparte un monto fijo o una tarifa de referencia.</span>
                    </div>
                  </label>
                </div>
              </div>
            ) : null}
            <label className="publication-field-label" htmlFor="pub-price">
              Precio referencial
            </label>
            <input
              id="pub-price"
              type="text"
              name="price"
              value={priceInputValue}
              onChange={handleChange}
              placeholder="Ej. 25,000 MXN"
              disabled={normalizedPriceSource === FIELD_SOURCE.UNIT}
            />
            {normalizedPriceSource === FIELD_SOURCE.UNIT ? (
              <p className="publication-field-hint">
                Mostraremos la tarifa guardada en la unidad para esta publicación.
              </p>
            ) : (
              <p className="publication-field-hint">
                Puedes indicar un monto estimado, rango o tarifa base.
              </p>
            )}
          </>
        ) : (
          <p className="publication-field-hint">
            Esta oferta se cotiza bajo solicitud. Tus clientes verán el botón para pedir cotización.
          </p>
        )}
        <div className="empresa-form-group full">
          <label htmlFor="pub-sla">
            SLA (Service Level Agreement) <span className="required-indicator" aria-hidden="true">*</span>
          </label>
          {hasLinkedUnit ? (
            <div
              className="publication-field-source publication-field-source--inline"
              role="radiogroup"
              aria-label="Fuente del SLA"
            >
              <label
                className={`publication-field-source__chip${
                  normalizedSlaSource === FIELD_SOURCE.UNIT ? ' is-active' : ''
                }${!canUseUnitSla ? ' is-disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="sla-source"
                  value={FIELD_SOURCE.UNIT}
                  checked={normalizedSlaSource === FIELD_SOURCE.UNIT}
                  onChange={() => handleSlaSourceChange(FIELD_SOURCE.UNIT)}
                  disabled={!canUseUnitSla}
                />
                <span>Usar SLA de la unidad</span>
              </label>
              <label
                className={`publication-field-source__chip${
                  normalizedSlaSource === FIELD_SOURCE.CUSTOM ? ' is-active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="sla-source"
                  value={FIELD_SOURCE.CUSTOM}
                  checked={normalizedSlaSource === FIELD_SOURCE.CUSTOM}
                  onChange={() => handleSlaSourceChange(FIELD_SOURCE.CUSTOM)}
                />
                <span>Definir SLA para la publicación</span>
              </label>
            </div>
          ) : null}
          <textarea
            id="pub-sla"
            name="sla"
            rows={4}
            value={slaFieldValue}
            onChange={handleChange}
            placeholder="Define tiempos de respuesta, cobertura o KPIs comprometidos."
            disabled={normalizedSlaSource === FIELD_SOURCE.UNIT}
            required
          />
          {normalizedSlaSource === FIELD_SOURCE.UNIT ? (
            <p className="publication-field-hint">
              Tomaremos automáticamente el SLA registrado en la unidad vinculada.
            </p>
          ) : (
            <p className="publication-field-hint">
              Define tiempos de respuesta, cobertura o KPIs comprometidos para tus clientes.
            </p>
          )}
        </div>
        {showPolicies ? (
          <div className="empresa-form-group full">
            <label htmlFor="pub-policies">Politicas</label>
            <textarea
              id="pub-policies"
              name="policies"
              rows={4}
              value={form.policies}
              onChange={handleChange}
              placeholder="Describe las politicas relevantes para el servicio."
            />
          </div>
        ) : null}
      </article>
    );

    const conversionTabContent = (
      <article className="publication-card">
        <header className="publication-card-head">
          <div>
            <h3>Preguntas clave para clientes</h3>
            <p>Responde dudas frecuentes antes de que tus clientes te contacten.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={handleAddFaq}>
            Anadir pregunta
          </button>
        </header>
        <section className="publication-faq-table" role="table">
          <header className="publication-faq-row publication-faq-row--header" role="row">
            <span role="columnheader">#</span>
            <span role="columnheader">Pregunta</span>
            <span role="columnheader">Respuesta</span>
            <span role="columnheader" className="publication-faq-actions-head">
              Acciones
            </span>
          </header>
          <div className="publication-faq-body">
            {form.faqs.map((faq, index) => (
              <div key={index} className="publication-faq-row" role="row">
                <span className="publication-faq-index-cell" role="cell">
                  {index + 1}
                </span>
                <div className="publication-faq-cell publication-faq-cell--question" role="cell">
                  <label className="sr-only" htmlFor={`faq-question-${index}`}>
                    Pregunta
                  </label>
                  <input
                    id={`faq-question-${index}`}
                    type="text"
                    value={faq.question}
                    onChange={event => handleFaqChange(index, 'question', event.target.value)}
                    placeholder="¿Cuál es el alcance del servicio?"
                  />
                  <small>Formula la pregunta tal como la expresaría tu cliente.</small>
                </div>
                <div className="publication-faq-cell publication-faq-cell--answer" role="cell">
                  <label className="sr-only" htmlFor={`faq-answer-${index}`}>
                    Respuesta
                  </label>
                  <textarea
                    id={`faq-answer-${index}`}
                    value={faq.answer}
                    onChange={event => handleFaqChange(index, 'answer', event.target.value)}
                    rows={3}
                    placeholder="Comparte la respuesta concreta que ofrecerá claridad."
                  />
                  <small>
                    Usa un tono directo e incluye datos accionables (entregas, coberturas,
                    excepciones).
                  </small>
                </div>
                <div className="publication-faq-actions" role="cell">
                  {form.faqs.length > 1 ? (
                    <button
                      type="button"
                      className="publication-remove-faq"
                      onClick={() => handleRemoveFaq(index)}
                    >
                      Quitar
                    </button>
                  ) : (
                    <span className="publication-faq-placeholder">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </article>
    );

    const tabs = [
      {
        id: 'general',
        label: 'Detalles principales',
        description: 'Define tipo, titulo y descripcion base de tu oferta.',
        content: generalTabContent,
      },
      {
        id: 'location',
        label: 'Zona de operacion',
        description: 'Indica la ciudad base o cobertura principal.',
        content: (
          <article className="publication-card">
            <header className="publication-card-head">
              <h3>Zona de operacion</h3>
              <p>Indica la ciudad base o ruta principal para que podamos mostrarte en mapas.</p>
            </header>
            <div className="publication-field full publication-field--location">
              <div className="publication-field-header">
                <label htmlFor="pub-location">
                  Ubicacion principal <span className="required-indicator" aria-hidden="true">*</span>
                </label>
                <button
                  type="button"
                  className="publication-field-tooltip"
                  title="Elige una opcion del listado para mostrar tu publicacion en el mapa. Si escribes un texto libre tendra menor alcance."
                  aria-label="Elige una opcion del listado para mostrar tu publicacion en el mapa. Si escribes un texto libre tendra menor alcance."
                >
                  <FiInfo aria-hidden="true" />
                </button>
              </div>
              <LocationAutocompleteInput
                id="pub-location"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="Ciudad, Estado / Pais"
              />
              <p className="publication-field-hint">
                Indica la ciudad, estado o zona donde estara disponible.
              </p>
            </div>
          </article>
        ),
      },
      {
        id: 'content',
        label: 'Imagen destacada',
        description: 'Configura el banner o foto principal.',
        content: contentTabContent,
      },
      {
        id: 'availability',
        label: 'Agenda',
        description: 'Configura fechas o periodo visible.',
        content: availabilityTabContent,
      },
      {
        id: 'conditions',
        label: 'Precio y SLA',
        description: 'Comparte tarifas y compromisos operativos.',
        content: conditionsTabContent,
      },
      {
        id: 'conversion',
        label: 'Preguntas clave',
        description: 'Anticipa dudas con preguntas y respuestas.',
        content: conversionTabContent,
      },
    ];

    const visibleTabs = shouldCaptureAvailability ? tabs : tabs.filter(tab => tab.id !== 'availability');
    const rawActiveTabIndex = visibleTabs.findIndex(tab => tab.id === activeTab);
    const activeTabIndex = rawActiveTabIndex === -1 ? 0 : rawActiveTabIndex;
    const isLastTab = activeTabIndex >= visibleTabs.length - 1;
    const handleNextStep = () => {
      if (isLastTab) return;
      setActiveTab(visibleTabs[activeTabIndex + 1].id);
    };
    const heroTitle = editingId ? 'Editar publicacion' : 'Crear publicacion';

    return (
      <div className="publication-form-layout">
        <section className="publication-form-view">
          <div className="publication-form-hero">
            <div className="publication-form-hero__content">
              <div className="publication-form-hero__copy">
                <p className="publication-form-hero__eyebrow">Promociona tu oferta</p>
                <h2>{heroTitle}</h2>
                <span>
                  Usa este configurador premium para que tus rutas, servicios y productos se vean igual
                  de profesionales que tu flota.
                </span>
              </div>
              <div
                className="publication-form-hero__actions"
                role="toolbar"
                aria-label="Acciones principales del formulario"
              >
                <button
                  type="button"
                  className="publication-form-action-link"
                  onClick={handleBackToSummary}
                >
                  Volver
                </button>
                <button
                  type="submit"
                  form="publication-form"
                  className="publication-form-action-btn"
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar publicacion' : 'Publicar'}
                </button>
              </div>
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <form id="publication-form" onSubmit={handleSubmit} className="publication-form">
            <div className="publication-form-body">
              <div className="publication-stepper-wrapper">
                <div className="publication-stepper">
                  <div
                    className="publication-stepper__tabs"
                    role="tablist"
                    aria-label="Pasos de la publicacion"
                  >
                    {visibleTabs.map((tab, index) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          id={`publication-tab-${tab.id}`}
                          type="button"
                          role="tab"
                          className={`publication-stepper-button${isActive ? ' active' : ''}`}
                          aria-selected={isActive}
                          aria-controls={`publication-panel-${tab.id}`}
                          tabIndex={isActive ? 0 : -1}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          <span className="publication-stepper-index">{index + 1}</span>
                          <span className="publication-stepper-copy">
                            <strong>{tab.label}</strong>
                            {tab.description ? <span>{tab.description}</span> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="publication-stepper__footer">
                    <button
                      type="button"
                      className="publication-stepper-next"
                      onClick={handleNextStep}
                      disabled={isLastTab}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
              <div className="publication-step-panel">
                {visibleTabs.map(tab =>
                  tab.id === activeTab ? (
                    <div
                      key={tab.id}
                      id={`publication-panel-${tab.id}`}
                      role="tabpanel"
                      aria-labelledby={`publication-tab-${tab.id}`}
                      className="publication-step-content"
                    >
                      {tab.content}
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </form>
          {editingId ? (
            <div className="publication-form-footer">
              <button
                type="button"
                className="publication-form-danger"
                onClick={() => handleDelete(editingId)}
                disabled={saving}
              >
                Eliminar publicacion
              </button>
            </div>
          ) : null}
        </section>
      </div>
    );
  };

  return (
    <div className="publication-shell">
      {mode === 'list' ? renderListView() : renderFormView()}
      <TableColumnMenu
        anchorElement={columnHeaderRefs.current[activeColumnMenu] || null}
        activeColumnKey={activeColumnMenu}
        columns={publicationColumns}
        onClose={() => setActiveColumnMenu('')}
        onMove={movePublicationColumn}
        onToggle={togglePublicationColumn}
        onWidthChange={setPublicationColumnWidth}
        onReset={resetPublicationColumns}
      />
      {showUnsavedPrompt ? (
        <div
          className="publication-unsaved-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="publication-unsaved-modal-title"
        >
          <div className="publication-unsaved-modal__backdrop" onClick={handleStayOnForm} />
          <div className="publication-unsaved-modal__dialog">
            <button
              type="button"
              className="publication-unsaved-modal__close"
              aria-label="Cerrar"
              onClick={handleStayOnForm}
            >
              ×
            </button>
            <div className="publication-unsaved-modal__content">
              <h3 id="publication-unsaved-modal-title">Hay cambios sin guardar</h3>
              <p>¿Deseas guardar tu avance como borrador antes de salir?</p>
            </div>
            <div className="publication-unsaved-modal__actions">
              <button
                type="button"
                className="btn publication-unsaved-modal__btn publication-unsaved-modal__btn--ghost"
                onClick={handleDiscardChanges}
              >
                No guardar
              </button>
              <button
                type="button"
                className="btn publication-unsaved-modal__btn publication-unsaved-modal__btn--primary"
                onClick={handleSaveDraftFromPrompt}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
